/**
 * Internal Ticketed-Events Capacity Reconciler API Contract (U3 — R17, R18,
 * R20, R41; KTD3)
 *
 * POST /internal/ticketed-events/reconcile-capacity
 *
 * Reports, and optionally repairs, the difference between the capacity counters
 * an event STORES and the ones its admissions IMPLY. The stored counters are a
 * cache; admissions plus unexpired capacity holds are the truth. Both numbers in
 * every row come out of the same SQL expression the repair writes with, so the
 * report and the repair cannot disagree about what "expected" means.
 *
 * Only the wire shape lives here. The endpoint's own error enum stays beside its
 * handler in the backend, like every other `/internal/*` contract in this folder.
 */

import { z } from 'zod'

export const ReconcileCapacityRequestSchema = z
  .object({
    /**
     * DEFAULTS TO A DRY RUN, and the default is the safety rail rather than a
     * convenience: an agent that forgets the field gets a report, never a write.
     */
    apply: z.boolean().default(false),
    /** Narrow to one event. */
    eventPublicId: z.string().min(1).optional(),
    /**
     * Narrow to one school. Given beside `eventPublicId`, the event must belong
     * to it — a scope that contradicts itself is a 404 rather than a run on
     * whichever id happened to win.
     */
    schoolPublicId: z.string().min(1).optional()
  })
  // AN APPLY MUST NAME ITS SCOPE, exactly as the sibling backfill endpoint
  // demands: a bare `{apply: true}` would rewrite every school's counters on one
  // request. A fleet-wide DRY RUN stays allowed — it is the P3 checkpoint.
  .refine(body => !body.apply || body.eventPublicId !== undefined || body.schoolPublicId !== undefined, {
    message: 'apply requires eventPublicId or schoolPublicId; a fleet-wide apply is not allowed over this endpoint',
    path: ['apply']
  })
export type ReconcileCapacityRequest = z.infer<typeof ReconcileCapacityRequestSchema>

/**
 * One ticket type whose stored counters disagree with its admissions. Only
 * drifting types are listed; a type in agreement produces no row.
 *
 * `expected*` is what the repair computes: `capacity_remaining =
 * GREATEST(capacity − live, 0)` and `over_capacity_count = GREATEST(live −
 * capacity, 0)`, where `live` counts `active` and `deactivated` admissions plus
 * unexpired capacity holds.
 */
export const ReconcileCapacityTicketTypeRowSchema = z.object({
  eventPublicId: z.string(),
  ticketTypePublicId: z.string(),
  capacity: z.number().int(),
  /** Seats the type is consuming right now: live admissions + unexpired holds. */
  liveSeats: z.number().int(),
  storedCapacityRemaining: z.number().int(),
  expectedCapacityRemaining: z.number().int(),
  storedOverCapacityCount: z.number().int(),
  expectedOverCapacityCount: z.number().int(),
  /** True when this run wrote the expected values; always false on a dry run. */
  repaired: z.boolean()
})
export type ReconcileCapacityTicketTypeRow = z.infer<typeof ReconcileCapacityTicketTypeRowSchema>

/**
 * An event whose own capacity columns disagree with the sum of its types' —
 * the invariant `resyncEventCapacityFromTicketTypes` maintains, checked against
 * the types' EXPECTED values so that "drift" means the same thing at both
 * levels: stored is not what a repair would leave behind.
 *
 * An event with no ticket types never produces a row: its stored capacity is
 * then the only limit it has and the resync deliberately leaves it alone.
 */
export const ReconcileCapacityEventRowSchema = z.object({
  eventPublicId: z.string(),
  storedTotalCapacity: z.number().int(),
  expectedTotalCapacity: z.number().int(),
  storedCapacityRemaining: z.number().int(),
  expectedCapacityRemaining: z.number().int(),
  storedOverCapacityCount: z.number().int(),
  expectedOverCapacityCount: z.number().int(),
  repaired: z.boolean()
})
export type ReconcileCapacityEventRow = z.infer<typeof ReconcileCapacityEventRowSchema>

/**
 * An event the repair refuses, with no drift figures and no write. The two
 * reasons are the repair's own refusal test, not the reconciler's: an event
 * whose confirmed order items are not fully minted yet (`incomplete_backfill`),
 * and one still holding an `awaiting_payment` order placed before the mint path
 * shipped (`legacy_unminted_hold`, SCH-1758). Deriving counters from admissions
 * on either shape would hand back seats that are genuinely sold.
 */
export const ReconcileCapacityRefusalSchema = z.object({
  eventPublicId: z.string(),
  reason: z.enum(['incomplete_backfill', 'legacy_unminted_hold']),
  /** Confirmed order items short of their `quantity` in purchase-origin admissions. */
  violations: z.number().int(),
  /** `awaiting_payment` orders on the event holding no admissions at all. */
  legacyHolds: z.number().int()
})
export type ReconcileCapacityRefusal = z.infer<typeof ReconcileCapacityRefusalSchema>

/**
 * The run report. The backend use-case returns exactly this type, so a field
 * added here without being populated there (or the reverse) fails to compile.
 *
 * The P3 checkpoint is a fleet-wide dry run with `eventsWithDrift` zero and no
 * refusal except `legacy_unminted_hold` (a pre-mint unpaid hold, refused by design).
 */
export const ReconcileCapacityResponseSchema = z.object({
  apply: z.boolean(),
  eventsScanned: z.number().int(),
  ticketTypesScanned: z.number().int(),
  /** Events with at least one drifting ticket type or a drifting event rollup. */
  eventsWithDrift: z.number().int(),
  /** Events the repair refused; each one is listed in `refusals` with its reason. */
  eventsRefused: z.number().int(),
  /** Events actually written. Always zero on a dry run. */
  eventsRepaired: z.number().int(),
  rows: z.array(ReconcileCapacityTicketTypeRowSchema),
  eventRows: z.array(ReconcileCapacityEventRowSchema),
  refusals: z.array(ReconcileCapacityRefusalSchema)
})
export type ReconcileCapacityResponse = z.infer<typeof ReconcileCapacityResponseSchema>
